/**
 * The project dashboard — four working modules, kept apart.
 *
 * The split is not merely menu tidying. The four modules are done by different
 * people at different times, and each has its own readiness condition: data
 * management is ready once wells carry curves and coordinates; formation
 * evaluation once a parameter set exists; 3D modelling only when markers reach
 * three wells or more; estimation only when some wells are the ones missing
 * curves.
 *
 * So each card shows readiness **together with its reason**. A module that is
 * not ready is not hidden — hiding it makes people think it does not exist,
 * when what is missing is one step that can simply be named.
 */
import { useNavigate } from "react-router-dom";

import { api } from "../api";
import { Button, Empty, Loading, Panel, StatusDot } from "../components/ui";
import { useAsync, useStored } from "../hooks";
import type { Project, ProjectDashboard, StoragePayload } from "../types";

function Stat({ label, value }: { label: string; value: string | number | null }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
      <span style={{ font: "400 10px var(--font-sans)", color: "var(--ink-6)" }}>{label}</span>
      <span style={{ font: "500 10.5px var(--font-mono)", color: "var(--ink-2)" }}>
        {value === null || value === undefined || value === "" ? "—" : value}
      </span>
    </div>
  );
}

export default function Dashboard({ project }: { project: Project }) {
  const navigate = useNavigate();
  // Panel Storage adalah blok diagnostik — engine, host, ukuran di disk, enam
  // tabel terbesar — dan ia dibaca ketika ada yang ditanyakan tentang basis
  // datanya, bukan tiap kali dasbor dibuka. Sepanjang layar yang ditempatinya
  // duduk di antara kartu modul dan daftar sumur yang kurang kurva: dua hal
  // yang justru dicari orang di sini. Keadaan terlipatnya disimpan, dengan
  // alasan yang sama seperti rel-rel layar lain.
  const [storageCollapsed, setStorageCollapsed] = useStored(
    "evolog.dashboard.storage.collapsed", false);
  const data = useAsync(
    (signal) =>
      api.get<ProjectDashboard>(`/api/projects/${project.id}/dashboard/`, {}, signal),
    [project.id],
  );

  const storage = useAsync(
    (signal) => api.get<StoragePayload>("/api/storage/", {}, signal),
    [],
  );

  if (data.loading) return <Loading label="Working out what each module is ready for…" />;
  if (data.error) return <Empty>Gagal memuat dasbor: {String(data.error)}</Empty>;
  const payload = data.data!;

  return (
    <div className="scroll-y" style={{ flex: 1, minHeight: 0, padding: 16 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ font: "600 15px var(--font-sans)", color: "var(--ink)" }}>
          {payload.project.name}
        </div>
        <div style={{ font: "400 10.5px var(--font-mono)", color: "var(--ink-6)" }}>
          {[payload.project.basin, payload.project.field, payload.project.crs]
            .filter(Boolean)
            .join(" · ")}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(310px, 1fr))",
          gap: 12,
          alignItems: "start",
        }}
      >
        {payload.modules.map((module) => (
          <Panel
            key={module.key}
            title={module.label}
            meta={module.ready ? "ready" : "not ready"}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <StatusDot tone={module.ready ? "ok" : "warn"} />
              <span
                style={{
                  font: "400 10px var(--font-sans)",
                  color: module.ready ? "var(--ink-5)" : "var(--warn-ink)",
                  lineHeight: 1.5,
                }}
              >
                {module.reason}
              </span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 4 }}>
              {Object.entries(module.stats).map(([label, value]) => (
                <Stat key={label} label={label} value={value} />
              ))}
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
              {module.screens.map((screen) => (
                <Button
                  key={screen.path}
                  variant="ghost"
                  onClick={() => navigate(`/projects/${project.id}/${screen.path}`)}
                >
                  {screen.label}
                </Button>
              ))}
            </div>
          </Panel>
        ))}
      </div>

      <Panel
        title="Storage"
        // `data?.database.vendor` menghentikan rantainya di `data` saja: muatan
        // yang datang tanpa `database` meledak di tengah render, dan yang
        // memutih adalah halaman pendaratan proyek — layar pertama yang dibuka
        // orang setiap pagi.
        meta={`${storage.data?.database?.vendor ?? "—"} · ${storage.data?.database?.size_label ?? "—"}`}
        collapsed={storageCollapsed}
        onCollapsedChange={setStorageCollapsed}
      >
        {/* Syaratnya `database`, bukan `data`: seluruh blok di bawah ini
            membacanya tanpa penjagaan, jadi muatan yang datang tanpa dia
            memindahkan ledakannya beberapa baris ke bawah alih-alih
            mencegahnya. */}
        {storage.data?.database && (
          <>
            <Stat label="Engine" value={storage.data.database.version} />
            <Stat label="Database" value={storage.data.database.name} />
            <Stat label="Host" value={storage.data.database.host} />
            <Stat label="Size on disk" value={storage.data.database.size_label} />
            <span style={{ font: "600 9px var(--font-mono)", color: "var(--ink-6)", marginTop: 6 }}>
              PROJECTS
            </span>
            {/* Lapangan yang disisihkan tidak didaftar di sini; ia dihitung
                saja, supaya tidak tampak terhapus. Mengaktifkannya lagi
                dari halaman Projects mengembalikannya ke daftar ini. */}
            {storage.data.projects.filter((row) => row.is_active).map((row) => (
              <Stat
                key={row.id}
                label={row.name}
                value={`${row.wells} wells · ${row.curves} curves · ${row.runs} runs`}
              />
            ))}
            {storage.data.projects.some((row) => !row.is_active) && (
              <Stat
                label="Set aside"
                value={`${storage.data.projects.filter((row) => !row.is_active).length} field(s), data kept`}
              />
            )}
            <span style={{ font: "600 9px var(--font-mono)", color: "var(--ink-6)", marginTop: 6 }}>
              LARGEST TABLES
            </span>
            {storage.data.database.tables.slice(0, 6).map((t) => (
              <Stat
                key={t.table}
                label={t.table}
                value={`${t.rows.toLocaleString()} rows${
                  t.bytes ? ` · ${(t.bytes / 1024 / 1024).toFixed(1)} MB` : ""
                }`}
              />
            ))}
            <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
              {storage.data.note}
            </span>
          </>
        )}
      </Panel>

      {payload.gaps.length > 0 && (
        <div style={{ marginTop: 14, maxWidth: 640 }}>
          <Panel title="Wells missing curves" meta={`${payload.gaps.length}`}>
            <span
              style={{ font: "400 10px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}
            >
              Curves that most wells in this field carry and these wells do not.
              The <b>missing</b> ones are not there at all; the <b>gappy</b>{" "}
              ones are there but not filled all the way — a third state that
              leaves the CPI sheet as holed as a curve that is not there at all,
              and one whose remedy differs: the missing are loaded, the gappy
              are estimated.
            </span>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {payload.gaps.map((gap) => (
                <div
                  key={gap.well_id}
                  style={{ display: "flex", justifyContent: "space-between", gap: 10 }}
                >
                  <span style={{ font: "500 10.5px var(--font-mono)", color: "var(--ink-3)" }}>
                    {gap.well}
                  </span>
                  <span style={{ display: "flex", gap: 8, minWidth: 0 }}>
                    {gap.missing.length > 0 && (
                      <span style={{ font: "400 10px var(--font-mono)", color: "var(--warn-ink)" }}>
                        {gap.missing.join(", ")}
                        {gap.missing_count > gap.missing.length
                          ? ` +${gap.missing_count - gap.missing.length}`
                          : ""}
                      </span>
                    )}
                    {gap.thin.length > 0 && (
                      <span
                        title="Present but not fully filled"
                        style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}
                      >
                        gappy: {gap.thin.join(", ")}
                        {gap.thin_count > gap.thin.length
                          ? ` +${gap.thin_count - gap.thin.length}`
                          : ""}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
            <Button variant="primary" onClick={() => navigate(`/projects/${project.id}/estimate`)}>
              Open the estimation module
            </Button>
          </Panel>
        </div>
      )}
    </div>
  );
}
